import { storedRef } from "@/components/ReferralKit";
import React, { useEffect, useState, useCallback } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { TeacherTour } from "@/components/TeacherTour";
import { motion } from "framer-motion";
import { Users, Plus, Copy, Check, GraduationCap, Flame, Trash2, ClipboardList, KeyRound, ShieldCheck, Lock, X, BellRing, Download, Link2, CalendarClock, Award, Printer, Music2 } from "lucide-react";
import { toast } from "sonner";
import { Navbar } from "@/components/Navbar";
import { ClassCountAlongs, countAlongSummary } from "@/components/ClassCountAlongs";
import { NudgedAgo } from "@/components/CountAlongClassTools";
import { HomeworkCalendar } from "@/components/HomeworkCalendar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { INSTRUMENTS, earnedCertificates } from "@/data/curriculum";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const daysSince = (d) => {
  if (!d) return Infinity;
  const then = new Date(d + "T00:00:00");
  const n = Math.floor((Date.now() - then.getTime()) / 86400000);
  return isNaN(n) ? Infinity : n;
};

const selectCls = "w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:border-primary focus:outline-none";
const CA_VIDEOS = ["2/2 and 6/8", ...[3, 4, 5, 6, 7, 8, 9, 10].map((n) => `Lesson ${n}`)];

const SchoolVerifyBanner = () => {
  const { reloadUser } = useAuth();
  const sent = useLocation().state?.verifySent;
  const [step, setStep] = useState(sent ? "code" : "email"); // email | code
  const [email, setEmail] = useState(sent || "");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(sent ? 30 : 0);

  useEffect(() => {
    if (sent) return;
    api.get("/teacher/verify-school/status").then(({ data }) => {
      if (data.pending_email) { setEmail(data.pending_email); setStep("code"); }
    }).catch(() => {});
  }, [sent]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const resend = async () => {
    setBusy(true);
    try {
      await api.post("/teacher/verify-school/request", { school_email: email.trim() });
      setCooldown(30);
      toast.success("New code sent — check your school inbox");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not resend code");
    } finally { setBusy(false); }
  };

  const request = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      await api.post("/teacher/verify-school/request", { school_email: email.trim() });
      setStep("code");
      setCooldown(30);
      toast.success("Verification code sent — check your school inbox");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send code");
    } finally { setBusy(false); }
  };

  const confirm = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setBusy(true);
    try {
      await api.post("/teacher/verify-school/confirm", { code: code.trim() });
      await reloadUser();
      toast.success("School email verified — classroom licences unlocked");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not verify");
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-8 rounded-2xl border border-border bg-primary/10/50 p-5" data-testid="school-verify-banner">
      <div className="flex items-center gap-2 text-primary">
        <ShieldCheck size={18} />
        <p className="font-serif text-lg">Verify your school email to unlock classroom licences</p>
      </div>
      {step === "email" ? (
        <>
          <p className="mt-1 text-sm text-muted-foreground">
            Enter your school/education email (e.g. ending in <span className="font-mono">.edu</span>, <span className="font-mono">.ac.uk</span> or <span className="font-mono">.sch.uk</span>). We'll email you a 6-digit code.
          </p>
          <form onSubmit={request} className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center">
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@yourschool.edu" data-testid="school-email-input" className="border-border bg-background sm:max-w-xs" />
            <Button type="submit" disabled={busy} data-testid="send-code-button" className="rounded-full bg-primary text-foreground hover:bg-primary/90">
              <KeyRound size={15} className="mr-1" /> {busy ? "Sending…" : "Send code"}
            </Button>
          </form>
        </>
      ) : (
        <>
          <p className="mt-2 font-serif text-base text-foreground" data-testid="check-inbox-heading">Check your school inbox</p>
          <p className="mt-1 text-sm text-muted-foreground">
            We sent a 6-digit code to <span className="font-medium">{email}</span>. Enter it below (it expires in 15 minutes).
          </p>
          <form onSubmit={confirm} className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center">
            <Input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="123456" inputMode="numeric" data-testid="school-code-input" className="border-border bg-background font-mono tracking-[0.4em] sm:max-w-[160px]" />
            <Button type="submit" disabled={busy} data-testid="confirm-code-button" className="rounded-full bg-primary text-foreground hover:bg-primary/90">
              {busy ? "Verifying…" : "Verify code"}
            </Button>
            <button type="button" onClick={resend} disabled={busy || cooldown > 0} data-testid="resend-code-button" className="text-sm text-primary hover:underline disabled:cursor-not-allowed disabled:text-muted-foreground disabled:no-underline">
              {cooldown > 0 ? `Resend code in ${cooldown}s` : "Resend code"}
            </button>
            <button type="button" onClick={() => setStep("email")} data-testid="resend-code-link" className="text-sm text-muted-foreground hover:text-primary hover:underline">Use a different email</button>
          </form>
        </>
      )}
    </div>
  );
};

const AssignForm = ({ classId, onAdded }) => {
  const [instId, setInstId] = useState(INSTRUMENTS[0].id);
  const [courseId, setCourseId] = useState(INSTRUMENTS[0].courses[0].id);
  const [target, setTarget] = useState("course");
  const [due, setDue] = useState("");
  const [busy, setBusy] = useState(false);

  const inst = INSTRUMENTS.find((i) => i.id === instId);
  const course = inst.courses.find((c) => c.id === courseId) || inst.courses[0];

  const changeInst = (id) => {
    setInstId(id);
    const first = INSTRUMENTS.find((i) => i.id === id).courses[0];
    setCourseId(first.id);
    setTarget("course");
  };

  const caPayload = (video, mode) => {
    const n = video === "2/2 and 6/8" ? 4 : Number(video.split(" ")[1]);
    const base = inst.courses[0];
    const lessonId = base.lessons[n - 1]?.id ?? null;
    return { type: "countalong", instrument_id: instId, course_id: base.id, lesson_id: lessonId, lesson_ids: lessonId ? [lessonId] : null,
             video, mode, title: `Count along · ${video}: ${mode === "challenge" ? "finish the 110 bpm challenge" : "watch to the end"}`, due_date: due || null };
  };

  const submit = async () => {
    setBusy(true);
    let payload;
    if (target.startsWith("pack|")) {
      const n = Number(target.split("|")[1]);
      const vids = [`Lesson ${n}`, ...(n === 3 || n === 4 ? ["2/2 and 6/8"] : [])];
      try {
        for (const v of vids) for (const m of ["watch", "challenge"]) onAdded((await api.post(`/classes/${classId}/assignments`, caPayload(v, m))).data);
        setDue("");
        toast.success(`Lesson ${n} count-along pack set: ${vids.length * 2} homework items`);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not set the whole pack");
      } finally { setBusy(false); }
      return;
    }
    if (target.startsWith("ca|")) {
      const [, video, mode] = target.split("|");
      payload = caPayload(video, mode);
    } else if (target === "course") {
      payload = { type: "course", instrument_id: instId, course_id: courseId, lesson_id: null,
                  lesson_ids: course.lessons.map((l) => l.id),
                  title: `${inst.label} — ${course.title}`, due_date: due || null };
    } else {
      const lessonId = Number(target);
      const idx = course.lessons.findIndex((l) => l.id === lessonId);
      const lesson = course.lessons[idx];
      payload = { type: "lesson", instrument_id: instId, course_id: courseId, lesson_id: lessonId,
                  lesson_ids: [lessonId],
                  title: `${inst.label} · Lesson ${idx + 1}: ${lesson.title}`, due_date: due || null };
    }
    try {
      const { data } = await api.post(`/classes/${classId}/assignments`, payload);
      onAdded(data);
      setDue("");
      toast.success("Assignment added");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not add");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid gap-2 sm:grid-cols-5">
      <select value={instId} onChange={(e) => changeInst(e.target.value)} className={selectCls} data-testid={`assignment-instrument-select-${classId}`}>
        {INSTRUMENTS.map((i) => <option key={i.id} value={i.id}>{i.label}</option>)}
      </select>
      <select value={courseId} onChange={(e) => { setCourseId(e.target.value); setTarget("course"); }} className={selectCls} data-testid={`assignment-course-select-${classId}`}>
        {inst.courses.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
      </select>
      <select value={target} onChange={(e) => setTarget(e.target.value)} className={selectCls} data-testid={`assignment-target-select-${classId}`}>
        <option value="course">Whole course</option>
        {course.lessons.map((l, i) => <option key={l.id} value={l.id}>Lesson {i + 1}: {l.title}</option>)}
        <optgroup label="Count-along homework packs (watch + challenge)">
          {[3, 4, 5, 6, 7, 8, 9, 10].map((n) => <option key={`pack-${n}`} value={`pack|${n}`}>Lesson {n} pack · {n === 3 || n === 4 ? 4 : 2} items</option>)}
        </optgroup>
        <optgroup label="Count-along homework">
          {CA_VIDEOS.flatMap((v) => [
            <option key={`${v}-w`} value={`ca|${v}|watch`}>Count along · {v} (watch)</option>,
            <option key={`${v}-c`} value={`ca|${v}|challenge`}>Count along · {v} (challenge)</option>,
          ])}
        </optgroup>
      </select>
      <input type="date" value={due} onChange={(e) => setDue(e.target.value)} className={selectCls} data-testid={`assignment-due-input-${classId}`} title="Optional due date" />
      <Button onClick={submit} disabled={busy} data-testid={`add-assignment-${classId}`} className="rounded-lg bg-primary text-foreground hover:bg-primary/90">
        <Plus size={15} className="mr-1" /> Assign
      </Button>
    </div>
  );
};

const LicenceForm = ({ classId, licensed, verified }) => {
  const [instId, setInstId] = useState(INSTRUMENTS[0].id);
  const [courseId, setCourseId] = useState(INSTRUMENTS[0].courses[0].id);
  const [busy, setBusy] = useState(false);
  const [refCode, setRefCode] = useState(storedRef());

  const inst = INSTRUMENTS.find((i) => i.id === instId);
  const key = `${instId}:${courseId}`;
  const owned = licensed.includes(key);

  if (!verified) {
    return (
      <p className="flex items-center gap-2 rounded-lg bg-primary/10/60 px-4 py-3 text-sm text-primary" data-testid={`licence-locked-${classId}`}>
        <Lock size={15} /> Verify your school email above to buy classroom licences.
      </p>
    );
  }

  const buy = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout-license", {
        class_id: classId, instrument_id: instId, course_id: courseId, referral_code: refCode.trim() || null,
        title: `${inst.label} — ${inst.courses.find((c) => c.id === courseId).title}`,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not start checkout");
      setBusy(false);
    }
  };

  return (
    <div className="grid gap-2 sm:grid-cols-3">
      <select value={instId} onChange={(e) => { setInstId(e.target.value); setCourseId(INSTRUMENTS.find((i) => i.id === e.target.value).courses[0].id); }} className={selectCls} data-testid={`licence-instrument-select-${classId}`}>
        {INSTRUMENTS.map((i) => <option key={i.id} value={i.id}>{i.label}</option>)}
      </select>
      <select value={courseId} onChange={(e) => setCourseId(e.target.value)} className={selectCls} data-testid={`licence-course-select-${classId}`}>
        {inst.courses.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
      </select>
      <Button onClick={buy} disabled={busy || owned} data-testid={`buy-licence-${classId}`} className="rounded-lg bg-secondary text-foreground hover:bg-muted">
        {owned ? "Already licensed" : `Unlock for class · £${refCode.trim() ? "350*" : "400"}`}
      </Button>
      <input value={refCode} onChange={(e) => setRefCode(e.target.value.toUpperCase())} placeholder="Referral code (optional, first licence: £350 + free 1:1)"
        data-testid={`licence-referral-${classId}`} className={`${selectCls} sm:col-span-3`} />
      {refCode.trim() && <p className="text-xs text-muted-foreground sm:col-span-3">* £350 applies if the code is valid and this is your first classroom licence — checked at checkout.</p>}
    </div>
  );
};

const AssignmentRow = ({ classId, a, onDelete, onNudged }) => {
  const [open, setOpen] = useState(false);
  const [students, setStudents] = useState(null);
  const [nudging, setNudging] = useState(null);

  useEffect(() => {
    if (open && students === null) {
      api.get(`/classes/${classId}/assignments/${a.id}/students`)
        .then(({ data }) => setStudents(data.students || []))
        .catch(() => setStudents([]));
    }
  }, [open, students, classId, a.id]);

  const nudge = async (sid) => {
    setNudging(sid);
    try {
      await api.post(`/classes/${classId}/assignments/${a.id}/nudge/${sid}`);
      toast.success("Reminder email sent");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send reminder");
    } finally { setNudging(null); setStudents(null); onNudged?.(); }
  };

  const nudgeAll = async () => {
    setNudging("all");
    try {
      const { data } = await api.post(`/classes/${classId}/assignments/${a.id}/nudge-all`);
      if (data.sent === 0 && data.failed === 0) toast.success("Everyone has completed this — no reminders needed");
      else toast.success(`Reminders sent to ${data.sent} student${data.sent === 1 ? "" : "s"}${data.failed ? ` (${data.failed} could not be emailed)` : ""}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send reminders");
    } finally { setNudging(null); setStudents(null); onNudged?.(); }
  };

  const total = a.total_students ?? 0;
  const done = a.completed_count ?? 0;

  return (
    <div className="rounded-lg border border-border bg-background" data-testid={`assignment-item-${a.id}`}>
      <div className="flex items-center justify-between px-4 py-2.5">
        <button onClick={() => setOpen((o) => !o)} data-testid={`assignment-toggle-${a.id}`} className="flex flex-1 flex-wrap items-center gap-2 text-left text-sm">
          <ClipboardList size={15} className="text-primary" />
          <span>{a.title}</span>
          <span className="rounded-full bg-secondary px-2 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">{a.type === "countalong" ? "count-along" : a.type}</span>
          {a.due_date && <span className="flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] text-primary"><CalendarClock size={11} /> Due {new Date(`${a.due_date}T00:00:00`).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })}</span>}
          <span className="flex items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] text-emerald-600 dark:text-emerald-400" data-testid={`assignment-progress-${a.id}`}>{done}/{total} done</span>
        </button>
        <button onClick={onDelete} data-testid={`delete-assignment-${a.id}`} className="ml-2 rounded-full p-1.5 text-red-600 dark:text-red-400 transition-colors hover:bg-red-500/15"><X size={15} /></button>
      </div>
      {open && (
        <div className="border-t border-border px-4 py-3" data-testid={`assignment-students-${a.id}`}>
          {students === null ? (
            <p className="text-xs text-muted-foreground">Loading…</p>
          ) : students.length === 0 ? (
            <p className="text-xs text-muted-foreground">No students in this class yet.</p>
          ) : (
            <>
              {students.some((s) => !s.done) && (
                <div className="mb-2 flex justify-end">
                  <button onClick={nudgeAll} disabled={nudging === "all"} data-testid={`nudge-all-${a.id}`} className="flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-[11px] text-primary transition-colors hover:border-primary disabled:opacity-50">
                    <BellRing size={12} /> {nudging === "all" ? "Sending…" : "Remind everyone not done"}
                  </button>
                </div>
              )}
              <ul className="space-y-1.5">
              {students.map((s) => (
                <li key={s.student_id} className="flex items-center justify-between text-sm" data-testid={`assignment-student-${a.id}-${s.student_id}`}>
                  <span className="flex items-center gap-2">
                    {s.done ? <Check size={14} className="text-emerald-600 dark:text-emerald-400" /> : <span className="h-3.5 w-3.5 rounded-full border border-input" />}
                    <span className={s.done ? "text-foreground" : "text-muted-foreground"}>{s.name || s.email}</span>
                  </span>
                  <span className="flex items-center gap-3">
                    {!s.done && <NudgedAgo n={s.last_nudged} testId={`assignment-nudged-${a.id}-${s.student_id}`} />}
                    <span className="font-mono text-xs text-muted-foreground">{s.done_count}/{s.total}</span>
                    {!s.done && (
                      <button
                        onClick={() => nudge(s.student_id)} disabled={nudging === s.student_id}
                        data-testid={`nudge-student-${a.id}-${s.student_id}`}
                        className="flex items-center gap-1 rounded-full border border-border px-2.5 py-1 text-[11px] text-primary transition-colors hover:border-primary disabled:opacity-50"
                      >
                        <BellRing size={11} /> {nudging === s.student_id ? "Sending…" : "Nudge"}
                      </button>
                    )}
                  </span>
                </li>
              ))}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const ClassCard = ({ cls, verified, onHomeworkChange, moveKey }) => {
  const [panel, setPanel] = useState(null);
  const [copied, setCopied] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [detail, setDetail] = useState(null);
  const [assignments, setAssignments] = useState(null);

  const loadDetail = useCallback(() => {
    api.get(`/classes/${cls.id}`).then(({ data }) => setDetail(data)).catch(() => setDetail({ students: [], licensed_courses: [] }));
  }, [cls.id]);
  const loadAssignments = useCallback(() => {
    api.get(`/classes/${cls.id}/assignments`).then(({ data }) => setAssignments(data.assignments || [])).catch(() => setAssignments([]));
  }, [cls.id]);

  useEffect(() => { loadDetail(); }, [loadDetail]);
  useEffect(() => { if (panel === "assignments" && assignments === null) loadAssignments(); }, [panel, assignments, loadAssignments]);
  useEffect(() => { if (moveKey) setAssignments(null); }, [moveKey]);

  const copy = () => {
    navigator.clipboard.writeText(cls.join_code).then(() => {
      setCopied(true); toast.success("Join code copied"); setTimeout(() => setCopied(false), 1500);
    });
  };

  const inviteLink = `${window.location.origin}/join/${cls.join_code}`;
  const copyInvite = () => {
    navigator.clipboard.writeText(inviteLink).then(() => {
      setLinkCopied(true); toast.success("Invite link copied — share it with your class"); setTimeout(() => setLinkCopied(false), 1500);
    });
  };

  const exportCsv = async () => {
    try {
      const res = await api.get(`/classes/${cls.id}/export`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `jt-hub-${cls.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch (e) { toast.error("Could not export CSV"); }
  };

  const offsets = detail?.reminder_offsets ?? [];
  const setOffset = async (val) => {
    const next = offsets.includes(val) ? offsets.filter((o) => o !== val) : [...offsets, val];
    try {
      const { data } = await api.patch(`/classes/${cls.id}`, { reminder_offsets: next });
      setDetail((d) => ({ ...d, reminder_offsets: data.reminder_offsets }));
    } catch (e) { toast.error("Could not update reminders"); }
  };

  const removeStudent = async (sid) => {
    try {
      await api.delete(`/classes/${cls.id}/students/${sid}`);
      setDetail((d) => ({ ...d, students: d.students.filter((s) => s.id !== sid) }));
      toast.success("Student removed");
    } catch (e) { toast.error("Could not remove student"); }
  };

  const deleteAssignment = async (aid) => {
    try {
      await api.delete(`/classes/${cls.id}/assignments/${aid}`);
      setAssignments((a) => a.filter((x) => x.id !== aid));
      onHomeworkChange?.();
      toast.success("Assignment removed");
    } catch (e) { toast.error("Could not remove assignment"); }
  };

  const students = detail?.students || [];
  const licensed = detail?.licensed_courses || [];
  const videos = detail?.countalong_videos || [];
  const quietCount = students.filter((s) => daysSince(s.last_active) >= 7).length;

  const Tab = ({ id, icon: Icon, label, count }) => (
    <button
      onClick={() => setPanel((p) => (p === id ? null : id))}
      data-testid={`tab-${id}-${cls.id}`}
      className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm transition-colors ${panel === id ? "bg-secondary text-foreground" : "border border-border text-muted-foreground hover:border-primary"}`}
    >
      <Icon size={14} /> {label}{typeof count === "number" ? ` (${count})` : ""}
    </button>
  );

  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-card" data-testid={`class-card-${cls.id}`}>
      <div className="flex flex-col gap-4 p-5 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <p className="font-serif text-xl text-foreground">{cls.name}</p>
          <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
            <span className="flex items-center gap-1.5"><Users size={13} /> {cls.student_count} student{cls.student_count === 1 ? "" : "s"}</span>
            {licensed.length > 0 && <span className="flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400"><ShieldCheck size={13} /> {licensed.length} licensed course{licensed.length === 1 ? "" : "s"}</span>}
            {quietCount > 0 && <span className="flex items-center gap-1.5 text-primary" data-testid={`quiet-count-${cls.id}`}><BellRing size={13} /> {quietCount} need a nudge</span>}
          </p>
        </div>
        <div className="flex flex-col items-stretch gap-2 sm:items-end">
          <button
            onClick={copy} data-testid={`copy-code-${cls.id}`}
            className="flex items-center justify-center gap-2 rounded-full border border-border bg-background px-4 py-2 font-mono text-sm tracking-widest text-primary transition-colors hover:border-primary"
          >
            {cls.join_code} {copied ? <Check size={14} /> : <Copy size={14} />}
          </button>
          <button
            onClick={copyInvite} data-testid={`copy-invite-${cls.id}`}
            className="flex items-center justify-center gap-1.5 rounded-full px-4 py-1.5 text-xs text-muted-foreground transition-colors hover:text-primary"
          >
            {linkCopied ? <Check size={13} /> : <Link2 size={13} />} Copy invite link
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 px-5 pb-4">
        <Tab id="roster" icon={Users} label="Roster" count={cls.student_count} />
        <Tab id="assignments" icon={ClipboardList} label="Assignments" />
        <Tab id="countalongs" icon={Music2} label="Count-alongs" />
        <Tab id="licences" icon={ShieldCheck} label="Licences" count={licensed.length} />
      </div>

      {panel === "roster" && (
        <div className="border-t border-border" data-testid={`roster-${cls.id}`}>
          {students.length === 0 ? (
            <p className="px-5 py-4 text-sm text-muted-foreground" data-testid={`roster-empty-${cls.id}`}>No students yet — share the join code so they can enrol.</p>
          ) : (
            <>
              <div className="flex justify-end gap-2 px-5 pt-3">
                <button onClick={() => window.print()} data-testid={`print-report-${cls.id}`} className="flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:border-primary hover:text-primary">
                  <Printer size={13} /> Print report card
                </button>
                <button onClick={exportCsv} data-testid={`export-csv-${cls.id}`} className="flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:border-primary hover:text-primary">
                  <Download size={13} /> Export CSV
                </button>
              </div>
              {students.some((s) => s.practice_week_minutes > 0) && (
                <div className="border-b border-border px-5 py-3 text-xs" data-testid={`top-practicers-${cls.id}`}>
                  <span className="font-mono uppercase tracking-widest text-primary">Top practice stars this week:</span>{" "}
                  {[...students].sort((a, b) => b.practice_week_minutes - a.practice_week_minutes).filter((s) => s.practice_week_minutes > 0).slice(0, 3)
                    .map((s, i) => <span key={s.id} className="ml-2 text-foreground">{["🥇", "🥈", "🥉"][i]} {s.name} · {s.practice_week_minutes} min</span>)}
                </div>
              )}
              <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-border text-[11px] uppercase tracking-widest text-muted-foreground">
                    <th className="px-5 py-3 font-mono font-normal">Student</th>
                    <th className="px-5 py-3 font-mono font-normal">Mastery</th>
                    <th className="px-5 py-3 font-mono font-normal">Lessons</th>
                    <th className="px-5 py-3 font-mono font-normal">Certificates</th>
                    <th className="px-5 py-3 font-mono font-normal">Streak</th>
                    <th className="px-5 py-3 font-mono font-normal">Practice</th>
                    <th className="px-5 py-3 font-mono font-normal">Count-alongs</th>
                    <th className="px-5 py-3 font-mono font-normal">Last active</th>
                    <th className="px-5 py-3" />
                  </tr>
                </thead>
                <tbody>
                  {students.map((s) => {
                    const quiet = daysSince(s.last_active) >= 7;
                    const certs = earnedCertificates(s.completed_lessons);
                    return (
                      <tr key={s.id} className="border-b border-border last:border-0" data-testid={`roster-row-${s.id}`}>
                        <td className="px-5 py-3">
                          <p className="flex items-center gap-2 font-medium text-foreground">
                            {s.name || "—"}
                            {quiet && <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-primary" data-testid={`quiet-badge-${s.id}`}>Quiet</span>}
                          </p>
                          <p className="text-xs text-muted-foreground">{s.email}</p>
                          <NudgedAgo n={s.last_nudged} testId={`roster-nudged-${s.id}`} />
                        </td>
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-2">
                            <div className="h-1.5 w-20 overflow-hidden rounded-full bg-secondary"><div className="h-full rounded-full bg-primary" style={{ width: `${s.mastery}%` }} /></div>
                            <span className="font-mono text-xs text-primary">{s.mastery}%</span>
                          </div>
                        </td>
                        <td className="px-5 py-3 font-mono text-xs">{s.lessons_done}/80</td>
                        <td className="px-5 py-3">
                          {certs.length > 0 ? (
                            <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary" title={certs.map((c) => c.title).join(", ")} data-testid={`certs-${s.id}`}>
                              <Award size={12} /> {certs.length}
                            </span>
                          ) : (
                            <span className="font-mono text-xs text-muted-foreground" data-testid={`certs-${s.id}`}>—</span>
                          )}
                        </td>
                        <td className="px-5 py-3"><span className="inline-flex items-center gap-1 font-mono text-xs"><Flame size={13} className="text-primary" /> {s.streak}</span></td>
                        <td className="px-5 py-3 font-mono text-xs" data-testid={`practice-${s.id}`} title={`${s.practice_total_minutes || 0} min in total`}>{s.practice_week_minutes || 0} min <span className="text-muted-foreground">/ 7d</span></td>
                        <td className="px-5 py-3 font-mono text-xs" data-testid={`countalong-summary-${s.id}`}>
                          <button type="button" onClick={() => setPanel("countalongs")} className="whitespace-nowrap hover:text-primary" data-testid={`countalong-summary-btn-${s.id}`}>{countAlongSummary(s, videos.length)}</button>
                        </td>
                        <td className="px-5 py-3 text-xs text-muted-foreground">{s.last_active || "—"}</td>
                        <td className="px-5 py-3 text-right">
                          <button onClick={() => removeStudent(s.id)} data-testid={`remove-student-${s.id}`} className="rounded-full p-1.5 text-red-600 dark:text-red-400 transition-colors hover:bg-red-500/15" title="Remove student"><Trash2 size={15} /></button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>

              {/* Print-only report card */}
              <div id="report-card-print" className="hidden" data-testid={`report-card-${cls.id}`}>
                <h1 style={{ fontSize: "24px", fontWeight: 700, marginBottom: "4px" }}>The JT Hub — Class Report Card</h1>
                <p style={{ marginBottom: "2px" }}>{cls.name}</p>
                <p style={{ color: "#555", marginBottom: "16px" }}>{new Date().toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })} · {students.length} students</p>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                  <thead>
                    <tr style={{ borderBottom: "2px solid #000", textAlign: "left" }}>
                      <th style={{ padding: "6px 8px" }}>Student</th>
                      <th style={{ padding: "6px 8px" }}>Mastery</th>
                      <th style={{ padding: "6px 8px" }}>Lessons</th>
                      <th style={{ padding: "6px 8px" }}>Certificates</th>
                      <th style={{ padding: "6px 8px" }}>Streak</th>
                      <th style={{ padding: "6px 8px" }}>Count-alongs</th>
                      <th style={{ padding: "6px 8px" }}>Last active</th>
                    </tr>
                  </thead>
                  <tbody>
                    {students.map((s) => (
                      <tr key={s.id} style={{ borderBottom: "1px solid #ccc" }}>
                        <td style={{ padding: "6px 8px" }}>{s.name || "—"}<br /><span style={{ color: "#666", fontSize: "11px" }}>{s.email}</span></td>
                        <td style={{ padding: "6px 8px" }}>{s.mastery}%</td>
                        <td style={{ padding: "6px 8px" }}>{s.lessons_done}/80</td>
                        <td style={{ padding: "6px 8px" }}>{earnedCertificates(s.completed_lessons).length}</td>
                        <td style={{ padding: "6px 8px" }}>{s.streak}</td>
                        <td style={{ padding: "6px 8px" }}>{countAlongSummary(s, videos.length)}{videos.length > 0 && s.countalong?.challenges.length === videos.length ? " · Speed Counter" : ""}</td>
                        <td style={{ padding: "6px 8px" }}>{s.last_active || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {panel === "countalongs" && <ClassCountAlongs classId={cls.id} students={students} videos={videos} onChanged={loadDetail} />}

      {panel === "assignments" && (
        <div className="border-t border-border p-5" data-testid={`assignments-${cls.id}`}>
          <div className="mb-4 rounded-lg border border-border bg-background p-3" data-testid={`reminders-${cls.id}`}>
            <p className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground"><BellRing size={12} className="text-primary" /> Auto-remind not-done students before the due date</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {[{ v: 7, l: "7 days" }, { v: 3, l: "3 days" }, { v: 1, l: "1 day" }, { v: 0, l: "Due day" }].map((o) => (
                <button key={o.v} onClick={() => setOffset(o.v)} data-testid={`reminder-offset-${cls.id}-${o.v}`}
                  className={`rounded-full px-3 py-1 text-xs transition-colors ${offsets.includes(o.v) ? "bg-primary text-foreground" : "border border-border text-muted-foreground hover:border-primary"}`}>
                  {o.l}
                </button>
              ))}
            </div>
          </div>
          <AssignForm classId={cls.id} onAdded={(a) => { setAssignments((prev) => [a, ...(prev || [])]); onHomeworkChange?.(); }} />
          <div className="mt-4 space-y-2">
            {assignments === null ? (
              <p className="text-sm text-muted-foreground">Loading assignments…</p>
            ) : assignments.length === 0 ? (
              <p className="text-sm text-muted-foreground" data-testid={`assignments-empty-${cls.id}`}>No assignments yet. Pick a course or lesson above to set your students a task.</p>
            ) : (
              assignments.map((a) => (
                <AssignmentRow key={a.id} classId={cls.id} a={a} onDelete={() => deleteAssignment(a.id)} onNudged={loadDetail} />
              ))
            )}
          </div>
        </div>
      )}

      {panel === "licences" && (
        <div className="border-t border-border p-5" data-testid={`licences-${cls.id}`}>
          {licensed.length > 0 && (
            <div className="mb-4 flex flex-wrap gap-2">
              {licensed.map((k) => (
                <span key={k} className="flex items-center gap-1.5 rounded-full bg-emerald-500/15 px-3 py-1 text-xs text-emerald-600 dark:text-emerald-400" data-testid={`licensed-course-${k}`}>
                  <ShieldCheck size={13} /> {k}
                </span>
              ))}
            </div>
          )}
          <p className="mb-3 text-sm text-muted-foreground">Buy a classroom licence (£400) to unlock a full course for every student in this class — now and in future.</p>
          <LicenceForm classId={cls.id} licensed={licensed} verified={verified} />
        </div>
      )}
    </div>
  );
};

export default function TeacherDashboard() {
  const { user } = useAuth();
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);
  const [calKey, setCalKey] = useState(0);
  const [moveKey, setMoveKey] = useState(0);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/classes");
      setClasses(data.classes || []);
    } catch (e) { setClasses([]); } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (user && (user.role === "teacher" || user.role === "admin")) load();
  }, [user, load]);

  if (user === null)
    return <div className="flex min-h-screen items-center justify-center bg-background"><GraduationCap className="animate-pulse text-primary" size={40} /></div>;
  if (user === false) return <Navigate to="/login" replace />;
  if (user.role !== "teacher" && user.role !== "admin") return <Navigate to="/dashboard" replace />;

  const createClass = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setCreating(true);
    try {
      const { data } = await api.post("/classes", { name: name.trim() });
      setClasses((c) => [data, ...c]);
      setName("");
      toast.success(`Class "${data.name}" created — share code ${data.join_code}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not create class");
    } finally { setCreating(false); }
  };

  return (
    <div className="min-h-screen text-foreground">
      <Navbar />
      <div className="mx-auto max-w-5xl px-4 sm:px-8 py-10 lg:py-14">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-primary">Class Teaching</p>
        <h1 className="mt-3 font-serif text-4xl tracking-tight sm:text-5xl">Your classroom</h1>
        <p className="mt-3 max-w-xl text-muted-foreground">
          Create a class, share its join code, set assignments and unlock courses for everyone with a classroom licence.
        </p>

        <TeacherTour />
        {!user.school_verified && <SchoolVerifyBanner />}
        {user.school_verified && (
          <p className="mt-6 inline-flex items-center gap-2 rounded-full bg-emerald-500/15 px-4 py-1.5 text-sm text-emerald-600 dark:text-emerald-400" data-testid="school-verified-badge">
            <ShieldCheck size={15} /> School verified — classroom licences enabled
          </p>
        )}

        <form onSubmit={createClass} className="mt-8 flex flex-col gap-3 rounded-2xl border border-border bg-card p-5 sm:flex-row sm:items-center">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Year 8 Music — Period 3" data-testid="class-name-input" className="border-border bg-background" maxLength={80} />
          <Button type="submit" disabled={creating} data-testid="create-class-button" className="rounded-full bg-primary text-foreground hover:bg-primary/90 sm:px-6">
            <Plus size={16} className="mr-1" /> {creating ? "Creating…" : "Create class"}
          </Button>
        </form>

        <div className="mt-8 space-y-4">
          {loading ? (
            <p className="text-sm text-muted-foreground" data-testid="classes-loading">Loading your classes…</p>
          ) : classes.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-border bg-card p-10 text-center" data-testid="classes-empty">
              <Users className="mx-auto text-primary" size={28} />
              <p className="mt-3 font-serif text-xl">No classes yet</p>
              <p className="mt-1 text-sm text-muted-foreground">Create your first class above to get a join code for your students.</p>
            </div>
          ) : (
            <>
              <HomeworkCalendar refreshKey={calKey} onMoved={() => setMoveKey((k) => k + 1)} />
              {classes.map((cls) => (
                <motion.div key={cls.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                  <ClassCard cls={cls} verified={!!user.school_verified} moveKey={moveKey} onHomeworkChange={() => setCalKey((k) => k + 1)} />
                </motion.div>
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
